import { useLazyQuery } from "@apollo/client/react";
import { graphql } from "gql.tada";
import { useEffect, useState } from "react";

const GET_PLAYERS_QUERY = graphql(`
  query players($ids: [ID!]!, $page: PageInfo!) {
    players(ids: $ids, page: $page) {
      nodes {
        id
        username
        ownerId
        customFields {
          property
          value
          name
          order
        }
        identities {
          properties {
            property
            value
          }
          organizationIdentityProvider {
            name
          }
        }
      }
    }
  }
`);

export const usePlayers = ({
  playerIds,
  skip,
}: {
  playerIds?: string[];
  skip?: boolean;
}) => {
  const [players, setPlayers] = useState<
    {
      id: unknown;
      username: string;
      ownerId: unknown;
      customFields: {
        property: string;
        value: string | null;
        name: string;
        order: number;
      }[];
      identities: {
        organizationIdentityProvider: {
          name: string;
        } | null;
        properties: {
          property: string;
          value: string;
        }[];
      }[];
    }[]
  >([]);
  const [loading, setLoading] = useState(!skip);

  const [fetch] = useLazyQuery<any>(GET_PLAYERS_QUERY);

  const refetch = async (playerIds: string[]) => {
    const remaining = [...playerIds];
    const result: {
      id: unknown;
      username: string;
      ownerId: unknown;
      customFields: {
        property: string;
        value: string | null;
        name: string;
        order: number;
      }[];
      identities: {
        organizationIdentityProvider: {
          name: string;
        } | null;
        properties: {
          property: string;
          value: string;
        }[];
      }[];
    }[] = [];
    while (remaining.length > 0) {
      result.push(
        ...((
          await fetch({
            variables: {
              page: { first: 100 },
              ids: remaining.splice(0, 100) as never,
            },
          })
        ).data?.players?.nodes.map((player: any) => ({
          ...player,
          identities: player.identities ?? [],
        })) ?? []),
      );
    }
    setPlayers(result);
    return result;
  };

  const playerIdsKey = playerIds ? playerIds.join(",") : null;

  useEffect(() => {
    if (!skip && playerIds) {
      setLoading(true);
      refetch(playerIds).finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, playerIdsKey]);

  return {
    results: players,
    refetch,
    loading,
  };
};
